import Heading from "@afa-shared/afa-components/dist/Heading";
import Text from "@afa-shared/afa-components/dist/Text";
import { fetchPageBySlug } from "@clients/contentfulClient";
import { StandardLink } from "@components/Link/StandardLink";
import RichTextRenderer from "@components/RichTextRenderer";
import Grid from "@components/client/Grid";
import ArticlePageWrapper from "@containers/Layout/PageWrapper/ArticlePageWrapper";
import { defaultContext } from "@utils/contextHelper/getInitialContext";
import { shouldRemoveBottomMargin } from "@utils/layoutHelpers/layoutMarginHelper";
import { getContentAreaMaxWidth } from "@utils/pageHelper";

import { UrlStructureService } from "services/UrlStructureService";
import { UrlStructureItem } from "types/UrlStructureItem";
import { Context } from "types/context/Context";

import CFContentArea from "../../components/cFContentArea";
import pageStyles from "../../styles/page.module.css";
import { StandardPageType_standardpage } from "../StandardPage/queries/contentfulTypes/StandardPageType";
import errorPageStyles from "./ErrorPage.module.css";

const ErrorPage = async ({
  errorCode,
  firstParentPage,
}: {
  errorCode: number;
  firstParentPage?: UrlStructureItem | undefined;
}) => {
  const urlStructure = await UrlStructureService(false);
  const slug = errorCode === 404 ? "sidan-hittades-inte" : "nagonting-gick-fel";
  const pageData = await fetchPageBySlug(urlStructure, slug, 1, false);
  const page: StandardPageType_standardpage = pageData?.slug?.data;
  const imageData = page?.ivMainImage?.[0];

  const context: Context = { ...defaultContext };

  const removeLayoutBottomMargin = shouldRemoveBottomMargin(page?.mainAreaBottomCollection?.items);

  return (
    <ArticlePageWrapper
      imageData={imageData}
      topImageText={page?.topImageText}
      columns={8}
      relatedContentHeading={page?.relatedContentHeading}
      relatedContentCollection={page?.relatedContentCollection}
      relatedContentSmallImages={page?.relatedContentSmallImages}
      context={context}
      useReCapcha={false}
      shouldRemoveBottomMargin={removeLayoutBottomMargin}
    >
      <Grid container mobile={12} desktop={8} desktopLarge={8}>
        <Heading variant="h1">{page?.heading}</Heading>
        {page?.preamble && (
          <Text
            variant={"preamble"}
            children={page?.preamble}
            style={{ marginBottom: "1.25rem" }}
          />
        )}

        <span className={errorPageStyles.errorCode}>Felkod {errorCode}</span>

        {errorCode === 404 && (
          <div className={errorPageStyles.parentLink}>
            {firstParentPage && (
              <Text variant="preamble">
                Kanske hittar du informationen du vill nå via sidan{" "}
                <StandardLink
                  href={firstParentPage?.url}
                  className={errorPageStyles.link}
                  text={firstParentPage?.name}
                />
              </Text>
            )}
          </div>
        )}

        <div className={pageStyles.contentAreaGrid}>
          {page?.mainBody?.json && (
            <RichTextRenderer
              json={page?.mainBody?.json}
              links={page?.mainBody?.links}
              pageTextArea
            />
          )}

          {page?.mainAreaCollection?.items?.length > 0 && (
            <CFContentArea
              desktopColumns={1}
              mobileColumns={1}
              tabletColumns={1}
              contentAreaItems={page?.mainAreaCollection?.items}
              maxWidth={getContentAreaMaxWidth(8)}
              context={context}
            />
          )}

          {page?.mainBodyBottom?.json && (
            <RichTextRenderer
              json={page?.mainBodyBottom?.json}
              links={page?.mainBodyBottom?.links}
              pageTextArea
            />
          )}

          {page?.mainAreaBottomCollection?.items?.length > 0 && (
            <CFContentArea
              desktopColumns={1}
              mobileColumns={1}
              tabletColumns={1}
              contentAreaItems={page?.mainAreaBottomCollection?.items}
              maxWidth={getContentAreaMaxWidth(8)}
              context={context}
            />
          )}
        </div>
      </Grid>
    </ArticlePageWrapper>
  );
};

export default ErrorPage;
